import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '../../components';
import api from '../../services/api';
import { formatCourseThumbnail, handleThumbnailError } from '../../utils/imageUtils';
import { useAuth } from '../../context/AuthContext';
import { formatDate, getCalendarParts, formatTime, formatShortDate } from '../../utils/dateUtils';
import { isValidPhone, sanitizePhone } from '../../utils/validationUtils';
import SEO from '../../components/common/SEO';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [allCourses, setAllCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [enrollmentRequests, setEnrollmentRequests] = useState([]);
  const [courseClasses, setCourseClasses] = useState({});

  // Enrollment modal state
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollCourseId, setEnrollCourseId] = useState(null);
  const [enrollCourseName, setEnrollCourseName] = useState('');
  const [enrollForm, setEnrollForm] = useState({ name: '', email: '', phone: '' });
  const [enrollErrors, setEnrollErrors] = useState({});
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [subscriptionData, coursesData, requestsData] = await Promise.all([
        api.getMySubscription().catch(() => ({ subscription: null, isActive: false })),
        api.getCourses({ limit: '100' }).catch(() => ({ courses: [] })),
        api.getMyEnrollmentRequests().catch(() => ({ requests: [] })),
      ]);

      setSubscription(subscriptionData.subscription);
      setIsSubscriptionActive(subscriptionData.isActive || false);
      setSubscriptionLoading(false);

      setAllCourses(coursesData.courses || []);
      setLoadingCourses(false);

      setEnrollmentRequests(requestsData.requests || []);

      // Fetch classes for subscribed courses
      if (subscriptionData.isActive && subscriptionData.subscription) {
        const allowedIds = getAllowedCourseIds(subscriptionData.subscription);
        const courses = coursesData.courses || [];
        const subscribedCourses = allowedIds.length > 0
          ? courses.filter(c => allowedIds.includes(c._id.toString()))
          : courses;

        const classResults = await Promise.all(
          subscribedCourses.map(c =>
            api.getClassesByCourse(c._id).then(d => ({ courseId: c._id, classes: d.classes || [] })).catch(() => ({ courseId: c._id, classes: [] }))
          )
        );
        const classMap = {};
        classResults.forEach(r => { classMap[r.courseId] = r.classes.filter(cls => cls.status === 'published'); });
        setCourseClasses(classMap);
      }
    };

    fetchData();
  }, []);

  const getAllowedCourseIds = (sub) => {
    if (!sub?.allowedCourses) return [];
    return sub.allowedCourses.map(c => (c._id || c).toString());
  };

  const allowedCourseIds = getAllowedCourseIds(subscription);

  // Split courses into "My Courses" (subscribed) and "Available" (rest)
  const myCourses = isSubscriptionActive
    ? (allowedCourseIds.length > 0
        ? allCourses.filter(c => allowedCourseIds.includes(c._id.toString()))
        : allCourses)
    : [];

  const myCoursesIds = new Set(myCourses.map(c => c._id.toString()));
  const availableCourses = allCourses.filter(c => !myCoursesIds.has(c._id.toString()));

  const getEnrollmentRequest = (courseId) =>
    enrollmentRequests.find(r => {
      const rCourseId = r.course?._id || r.course;
      return rCourseId?.toString() === courseId.toString();
    });

  const openEnrollModal = (course) => {
    setEnrollCourseId(course._id);
    setEnrollCourseName(course.title);
    setEnrollForm({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '' });
    setShowEnrollModal(true);
  };

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!user?.phone && !isValidPhone(enrollForm.phone)) errors.phone = 'Please enter a valid phone number (7-15 digits)';
    if (Object.keys(errors).length > 0) { setEnrollErrors(errors); return; }
    setEnrollErrors({});
    setEnrolling(true);
    try {
      const data = await api.createEnrollmentRequest({
        courseId: enrollCourseId,
        name: enrollForm.name,
        email: enrollForm.email,
        phone: enrollForm.phone,
      });
      setEnrollmentRequests(prev => [...prev, data.request]);
      setShowEnrollModal(false);
      alert('Enrollment request submitted! We will process it shortly.');
    } catch (err) {
      alert(err.message || 'Failed to submit enrollment request');
    } finally {
      setEnrolling(false);
    }
  };

  const getUpcomingClass = (courseId) => {
    const classes = courseClasses[courseId] || [];
    const now = new Date();
    return classes
      .filter(cls => new Date(cls.scheduledDate) >= now)
      .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))[0];
  };

  return (
    <div className="min-h-screen bg-dark-50">
      <SEO title="Student Dashboard" noIndex />
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-dark-900 mb-1">
              {user?.name ? `Welcome, ${user.name}` : 'Student Dashboard'}
            </h1>
            {!subscriptionLoading && (
              <p className="text-dark-500">
                {isSubscriptionActive
                  ? 'Browse your courses and attend scheduled classes.'
                  : 'Explore available courses and request enrollment to get started.'}
              </p>
            )}
          </div>

          {/* Subscription Status */}
          {!subscriptionLoading && (
            <div className={`mb-8 p-3 rounded-lg border ${isSubscriptionActive ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center gap-2">
                {isSubscriptionActive ? (
                  <>
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium text-green-800">
                      Active Subscription{subscription && ` (Valid until ${formatDate(subscription.endDate)})`}
                    </span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium text-amber-800">
                      No active subscription -- Enroll in a course below and the admin will activate your access.
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {loadingCourses ? (
            <div className="text-center py-12 text-dark-500">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-3" />
              Loading courses...
            </div>
          ) : (
            <>
              {/* Section: My Courses (subscribed students only) */}
              {isSubscriptionActive && myCourses.length > 0 && (
                <div className="mb-10 space-y-6">
                  <h2 className="text-xl font-bold text-dark-900">My Courses</h2>
                  {myCourses.map((course) => {
                    const classes = (courseClasses[course._id] || [])
                      .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
                    const now = new Date();
                    const upcomingClasses = classes.filter(c => new Date(c.scheduledDate) >= now);
                    const pastClasses = classes.filter(c => new Date(c.scheduledDate) < now);

                    return (
                      <div key={course._id} className="card overflow-hidden border border-primary-100">
                        {/* Course header row */}
                        <div className="flex flex-col sm:flex-row">
                          <img
                            src={formatCourseThumbnail(course.thumbnail)}
                            alt={course.title}
                            className="w-full sm:w-48 h-36 object-cover flex-shrink-0"
                            onError={handleThumbnailError}
                          />
                          <div className="p-5 flex-1 flex flex-col justify-between">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h3 className="text-lg font-bold text-dark-900">{course.title}</h3>
                                <span className="bg-primary-100 text-primary-700 text-xs font-medium px-2 py-0.5 rounded-full">Subscribed</span>
                              </div>
                              {course.teacher?.name && (
                                <p className="text-sm text-dark-500 mb-2">by {course.teacher.name}</p>
                              )}
                              {course.shortDescription && (
                                <p className="text-sm text-dark-400 line-clamp-2">{course.shortDescription}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-3">
                              <Link to={`/student/courses/${course._id}`} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                                View Course & Classes
                              </Link>
                              {classes.length > 0 && (
                                <span className="text-xs text-dark-400">{classes.length} class{classes.length !== 1 ? 'es' : ''}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Classes schedule */}
                        {classes.length > 0 && (
                          <div className="border-t border-dark-100">
                            <div className="px-5 py-3 bg-dark-50 flex items-center gap-2">
                              <svg className="w-4 h-4 text-dark-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                              <span className="text-sm font-semibold text-dark-700">Live Classes</span>
                            </div>
                            <div className="divide-y divide-dark-100">
                              {upcomingClasses.map((cls, i) => {
                                const isNext = i === 0;
                                const classDate = new Date(cls.scheduledDate);
                                const parts = getCalendarParts(cls.scheduledDate);
                                return (
                                  <div key={cls._id} className={`px-5 py-3.5 flex items-start gap-4 ${isNext ? 'bg-primary-50/50' : 'hover:bg-dark-50'} transition-colors`}>
                                    {/* Date block */}
                                    <div className={`flex-shrink-0 w-14 text-center rounded-lg py-1.5 ${isNext ? 'bg-primary-500 text-white' : 'bg-dark-100 text-dark-600'}`}>
                                      <p className="text-xs font-medium leading-tight">{parts.month}</p>
                                      <p className="text-lg font-bold leading-tight">{parts.day}</p>
                                    </div>
                                    {/* Class info */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="text-sm font-semibold text-dark-900">{cls.title}</p>
                                        {isNext && <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-medium">Next</span>}
                                      </div>
                                      <p className="text-xs text-dark-500 mt-0.5">
                                        {parts.weekday}
                                        {' · '}
                                        {formatTime(cls.scheduledDate)}
                                        {cls.duration ? ` · ${cls.duration} min` : ''}
                                        {cls.teacher && typeof cls.teacher === 'object' ? ` · ${cls.teacher.name}` : ''}
                                      </p>
                                    </div>
                                    {/* Join button */}
                                    {cls.meetingLink ? (
                                      <a href={cls.meetingLink} target="_blank" rel="noopener noreferrer"
                                        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                                          isNext ? 'btn-primary' : 'border border-primary-300 text-primary-600 hover:bg-primary-50'
                                        }`}>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                        Join
                                      </a>
                                    ) : (
                                      <span className="flex-shrink-0 text-xs text-dark-400 py-2">No link yet</span>
                                    )}
                                  </div>
                                );
                              })}
                              {pastClasses.length > 0 && (
                                <div className="px-5 py-3">
                                  <details className="group">
                                    <summary className="text-xs font-medium text-dark-400 cursor-pointer hover:text-dark-600 list-none flex items-center gap-1">
                                      <svg className="w-3.5 h-3.5 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                      {pastClasses.length} past class{pastClasses.length !== 1 ? 'es' : ''}
                                    </summary>
                                    <div className="mt-2 space-y-2">
                                      {pastClasses.map((cls) => {
                                        const parts = getCalendarParts(cls.scheduledDate);
                                        return (
                                          <div key={cls._id} className="flex items-center gap-3 text-dark-400 text-sm py-1">
                                            <span className="w-14 text-center text-xs flex-shrink-0">
                                              {parts.month} {parts.day}
                                            </span>
                                            <span className="line-through">{cls.title}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </details>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {classes.length === 0 && (
                          <div className="border-t border-dark-100 px-5 py-6 text-center text-sm text-dark-400">
                            No classes scheduled yet for this course.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Section: Available Courses */}
              {availableCourses.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-dark-900 mb-4">
                    {isSubscriptionActive ? 'Other Available Courses' : 'Available Courses'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {availableCourses.map((course) => {
                      const request = getEnrollmentRequest(course._id);
                      const hasActiveRequest = request && request.status !== 'rejected';
                      return (
                        <div key={course._id} className="card overflow-hidden">
                          <div className="relative">
                            <img
                              src={formatCourseThumbnail(course.thumbnail)}
                              alt={course.title}
                              className="w-full h-44 object-cover"
                              onError={handleThumbnailError}
                            />
                            {course.price === 0 && (
                              <div className="absolute top-3 left-3 bg-green-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                                Free
                              </div>
                            )}
                          </div>
                          <div className="p-5 space-y-3">
                            <h3 className="text-lg font-semibold text-dark-900 line-clamp-2">{course.title}</h3>
                            {course.teacher?.name && (
                              <p className="text-sm text-dark-500">by {course.teacher.name}</p>
                            )}
                            {course.shortDescription && (
                              <p className="text-sm text-dark-400 line-clamp-2">{course.shortDescription}</p>
                            )}

                            {course.price > 0 && (
                              <div className="flex items-baseline gap-2">
                                <span className="text-lg font-bold text-dark-900">${course.price}</span>
                                {course.originalPrice > course.price && (
                                  <span className="text-sm text-dark-400 line-through">${course.originalPrice}</span>
                                )}
                              </div>
                            )}

                            {hasActiveRequest ? (
                              <div className={`text-center py-2.5 rounded-lg text-sm font-medium ${
                                request.status === 'approved'
                                  ? 'bg-green-50 text-green-700 border border-green-200'
                                  : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                              }`}>
                                <div className="flex items-center justify-center gap-1.5">
                                  {request.status === 'approved' ? (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                  )}
                                  {request.status === 'approved' ? 'Enrollment Approved' : 'Request Processing'}
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => openEnrollModal(course)}
                                className="btn-primary w-full text-center text-sm py-2.5"
                              >
                                Enroll Now
                              </button>
                            )}

                            <Link
                              to={`/courses/${course._id}`}
                              className="text-primary-500 hover:text-primary-700 text-sm font-medium text-center block"
                            >
                              View Details
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No courses at all */}
              {allCourses.length === 0 && (
                <div className="text-center py-16 text-dark-400">
                  <svg className="w-16 h-16 mx-auto mb-4 text-dark-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                  <p className="font-medium text-dark-500">No courses available yet</p>
                  <p className="text-sm mt-1">Check back soon for new courses.</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Enrollment Request Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h2 className="text-xl font-bold text-dark-900 mb-1">Enroll in Course</h2>
            <p className="text-sm text-dark-500 mb-5">
              Submit your details to request enrollment in <span className="font-medium text-dark-700">{enrollCourseName}</span>.
            </p>
            <form onSubmit={handleEnrollSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Full Name</label>
                <input type="text" value={enrollForm.name} readOnly disabled className="w-full px-4 py-2.5 border border-dark-200 rounded-lg bg-dark-50 text-dark-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Email</label>
                <input type="email" value={enrollForm.email} readOnly disabled className="w-full px-4 py-2.5 border border-dark-200 rounded-lg bg-dark-50 text-dark-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                {user?.phone ? (
                  <input type="tel" value={enrollForm.phone} readOnly disabled className="w-full px-4 py-2.5 border border-dark-200 rounded-lg bg-dark-50 text-dark-500 cursor-not-allowed" />
                ) : (
                  <>
                    <input
                      type="tel"
                      value={enrollForm.phone}
                      onChange={(e) => { setEnrollForm(prev => ({ ...prev, phone: sanitizePhone(e.target.value) })); setEnrollErrors(prev => ({ ...prev, phone: null })); }}
                      required
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${enrollErrors.phone ? 'border-red-400' : 'border-dark-200'}`}
                      placeholder="+91 98765 43210"
                    />
                    {enrollErrors.phone ? (
                      <p className="text-xs text-red-500 mt-1">{enrollErrors.phone}</p>
                    ) : (
                      <p className="text-xs text-dark-400 mt-1">Include country code, e.g. +91 98765 43210</p>
                    )}
                  </>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowEnrollModal(false)} className="flex-1 px-4 py-2.5 border border-dark-200 rounded-lg hover:bg-dark-50 text-sm font-medium">
                  Cancel
                </button>
                <button type="submit" disabled={enrolling} className="flex-1 btn-primary px-4 py-2.5 text-sm font-medium disabled:opacity-50">
                  {enrolling ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default StudentDashboard;
